import IMAGES from '@/assets/images';
import CustomImage from '@/components/CustomImage';
import { Flex, type TableProps } from 'antd';
import { useGetWorkingReportQuery } from '../../../../app/features/employee/emp-attendance/emp-attendance.api';
import type {
  WorkingReportData,
  WorkingReportParams,
} from '../../../../app/features/employee/emp-attendance/emp-attendance.type';
import CustomTable from '../../../../components/CustomTable';
import useAuth from '../../../../hooks/useAuth';
import useError from '../../../../hooks/useError';
import useFilters from '../../../../hooks/useFilters';
import { useQueryFilters } from '../../../../hooks/useQueryFilters';
import TopBar from './_components/WorkTimeReport/TopBar';

// Helper function to format minutes to hours and minutes
const formatMinutes = (minutes: number) => {
  if (minutes === 0) return '0H 0M';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}H ${mins}M`;
};

// Helper function to format time
const formatTime = (dateString: string | null) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default function WorkTimeReport() {
  const { organizationId } = useAuth();
  const {
    page,
    limit,
    filter,
    handlePagination,
    handleLimit,
    resetPagination,
  } = useFilters<WorkingReportParams>();
  const { filters: queryFilter } = useQueryFilters<WorkingReportParams>(
    {},
    resetPagination,
  );
  const { data, isLoading, isFetching, error } = useGetWorkingReportQuery({
    params: {
      ...filter,
      ...queryFilter,
      organizationId,
    },
  });
  useError(error, 'Failed to fetch working report data');

  const loading = isLoading || isFetching;

  const columns: TableProps<WorkingReportData['data'][0]>['columns'] = [
    {
      title: 'ID',
      dataIndex: 'employee_id',
      align: 'center',
      render: (val: string) => val ?? 'N/A',
    },
    {
      title: 'Image',

      align: 'center',
      render: (record) => {
        return (
          <Flex align="center" justify="center">
            <CustomImage
              width={32}
              height={32}
              alt={record.name}
              src={record.photo || IMAGES.avatar}
              className="mx-auto rounded-full object-cover"
            />
          </Flex>
        );
      },
    },
    {
      title: 'Date',
      dataIndex: 'date',
      render: (val: string) => val ?? 'N/A',
    },
    {
      title: 'Employee name',
      dataIndex: 'name',
      align: 'center',
      render: (val: string) => val ?? 'N/A',
    },
    {
      title: 'Check In',
      dataIndex: 'checkInTime',
      render: (val: string | null) => formatTime(val),
    },
    {
      title: 'Check Out',
      dataIndex: 'checkOutTime',
      render: (val: string | null) => formatTime(val),
    },
    {
      title: 'Work Hours',
      dataIndex: 'workingHoursInMinutes',
      render: (val: number) => formatMinutes(val),
    },
    {
      title: 'Breaks',
      dataIndex: 'breakTimeInMinutes',
      render: (val: number) => formatMinutes(val),
    },
    {
      title: 'Idle Time',
      dataIndex: 'idleTimeInMinutes',
      align: 'center',
      render: (val: number) => formatMinutes(val),
    },
    {
      title: 'Over Time',
      dataIndex: 'overtimeInMinutes',
      align: 'center',
      render: (val: number) => formatMinutes(val),
    },
    // {
    //   title: "Actions",
    //   dataIndex: "userId",
    //   align: "end",
    //   render: (val: string) => (
    //     <Link to={`${val}`}>
    //       <Button className="action-btn" size="small" icon={<ViewIcon />} />
    //     </Link>
    //   ),
    // },
  ];

  return (
    <>
      <TopBar data={data?.data?.dashboard} isLoading={loading} />

      <CustomTable
        tableRootClassName="!border-0 !p-0"
        customTitle="Worktime Breakdown"
        columns={columns}
        dataSource={data?.data?.data || []}
        hideRowSelect
        total={data?.data?.count || 0}
        limit={limit}
        page={page}
        loading={loading}
        onLimitChange={handleLimit}
        onPaginate={handlePagination}
      />
    </>
  );
}
